import React from 'react';
import cn from 'classnames';
import moment from 'moment';
import './OfficeClockDayPicker.scss';

import { TIME_RANGE, isBlackoutDate } from '../../helpers/office_helpers';

/*
 * Horizontal scrollable strip of weekday pills.
 * Each pill shows the day abbreviation, number, and a green dot if the user has a booking.
 * Days beyond the user's max_advance_days are greyed out and unclickable.
 * Selecting "today" snaps the time to now; other days default to 9 AM.
 */
const OfficeClockDayPicker = ({
  allDays,
  announcements,
  selectedDate,
  setSelectedDate,
  setSelectedTime,
  setSelectedEndTime,
  maxAdvanceDays,
  bookedDates,
  daysScrollRef,
  canScrollLeft,
  canScrollRight,
  scrollDays
}) => {
  const handleDayClick = d => {
    setSelectedDate(d.value);
    setSelectedEndTime(null);
    const isClickToday = d.value === moment().format('YYYY-MM-DD');
    if (isClickToday) {
      const n = moment();
      const mins = n.minutes() < 30 ? '00' : '30';
      const h = Math.max(TIME_RANGE.start, Math.min(n.hours(), TIME_RANGE.end - 1));
      setSelectedTime(`${String(h).padStart(2, '0')}:${mins}`);
    } else {
      setSelectedTime('09:00');
    }
  };

  return (
    <div className='clock-days-outer'>
      <div className='clock-days' ref={daysScrollRef}>
        {allDays.map(d => {
          const beyondLimit = d.daysOut > maxAdvanceDays;
          const hasBookableAnnouncement = announcements.some(a => a.date === d.value && a.allowEarlyBooking);
          const isBlackout = isBlackoutDate(d.value);
          const canSelect = !isBlackout && (hasBookableAnnouncement || !beyondLimit);
          return (
            <React.Fragment key={d.value}>
              {d.weekBreak && <div className='week-divider' />}
              <button
                className={cn('day-pill', { active: selectedDate === d.value, 'has-booking': bookedDates.has(d.value), 'beyond-limit': !canSelect })}
                onClick={() => canSelect && handleDayClick(d)}
                disabled={!canSelect}
              >
                <span className='day-weekday'>{d.weekday}</span>
                <span className='day-num'>{d.dayNum}</span>
                {bookedDates.has(d.value) && <span className='day-booking-dot' />}
              </button>
            </React.Fragment>
          );
        })}
      </div>
      {canScrollLeft && (
        <button className='scroll-arrow left' onClick={() => scrollDays('left')}>
          &lsaquo;
        </button>
      )}
      {canScrollRight && (
        <button className='scroll-arrow right' onClick={() => scrollDays('right')}>
          &rsaquo;
        </button>
      )}
      {canScrollLeft && <div className='scroll-fade left' />}
      {canScrollRight && <div className='scroll-fade right' />}
    </div>
  );
};

export default OfficeClockDayPicker;
